import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { client } from "@client/client"
import { CLASS_LEVELS } from "@client/lib/class-levels"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { uploadFile, type UploadedFile } from "@client/lib/uploads"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"

type Step = "basics" | "family" | "school" | "sponsorship" | "documents" | "review"

const STEPS: { key: Step; label: string }[] = [
  { key: "basics", label: "Basics" },
  { key: "family", label: "Family & guardian" },
  { key: "school", label: "School & academics" },
  { key: "sponsorship", label: "Sponsorship" },
  { key: "documents", label: "Documents" },
  { key: "review", label: "Review" },
]

const GENDERS = ["Male", "Female"] as const

const RELATIONSHIPS = [
  "Parent", "Guardian", "Grandparent", "Uncle", "Aunt", "Sibling", "Other"
] as const

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function SaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  )
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

interface FormData {
  // Basics
  fullName: string
  preferredName: string
  dateOfBirth: string
  gender: string
  photo: UploadedFile | null
  photoPreview: string | null
  // Family & Guardian
  guardianName: string
  guardianPhone: string
  guardianEmail: string
  guardianRelationship: string
  householdSize: string
  address: string
  familyCircumstances: string
  // School & Academics
  school: string
  classLevel: string
  state: string
  lga: string
  // Sponsorship
  needsSponsorship: boolean
  sponsorshipNotes: string
  // Documents
  documents: { name: string; file: UploadedFile }[]
}

const initialFormData: FormData = {
  fullName: "",
  preferredName: "",
  dateOfBirth: "",
  gender: "Male",
  photo: null,
  photoPreview: null,
  guardianName: "",
  guardianPhone: "",
  guardianEmail: "",
  guardianRelationship: "Parent",
  householdSize: "",
  address: "",
  familyCircumstances: "",
  school: "",
  classLevel: "Primary 1",
  state: "Edo",
  lga: "",
  needsSponsorship: true,
  sponsorshipNotes: "",
  documents: [],
}

export function AddChildPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const canMutate = useHasPermission("children_mutate")
  const draftQueryId = searchParams.get("draft")
  const [currentStep, setCurrentStep] = useState<Step>("basics")
  const [form, setForm] = useState<FormData>(initialFormData)
  const [draftId, setDraftId] = useState<string | null>(draftQueryId)
  const [saving, setSaving] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [loadingDraft, setLoadingDraft] = useState(Boolean(draftQueryId))
  const [photoUploading, setPhotoUploading] = useState(false)
  const [documentUploading, setDocumentUploading] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!draftQueryId) {
      setDraftId(null)
      setLoadingDraft(false)
      return
    }

    let cancelled = false
    setLoadingDraft(true)
    void client.api.admin.children.drafts[":id"].$get({ param: { id: draftQueryId } })
      .then(async (res) => {
        const body = await res.json()
        if (cancelled) return
        if (!res.ok || !("data" in body)) {
          toast.error(getErrorMessage(body))
          return
        }
        const draftData = body.data.data as Partial<FormData>
        setDraftId(body.data.id)
        setForm({
          ...initialFormData,
          ...draftData,
          photo: draftData.photo ?? null,
          photoPreview: draftData.photoPreview ?? null,
          documents: Array.isArray(draftData.documents) ? draftData.documents : [],
        })
        toast.success("Draft loaded")
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load draft")
      })
      .finally(() => {
        if (!cancelled) setLoadingDraft(false)
      })

    return () => {
      cancelled = true
    }
  }, [draftQueryId])

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep)
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === STEPS.length - 1

  const handlePrevious = () => {
    if (!isFirstStep && currentStepIndex > 0) {
      const prevStep = STEPS[currentStepIndex - 1]
      if (prevStep) setCurrentStep(prevStep.key)
    }
  }

  const handleNext = () => {
    if (!isLastStep && currentStepIndex < STEPS.length - 1) {
      const nextStep = STEPS[currentStepIndex + 1]
      if (nextStep) setCurrentStep(nextStep.key)
    }
  }

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || photoUploading) return

    setForm((current) => ({ ...current, photo: null }))
    const reader = new FileReader()
    reader.onloadend = () => {
      setForm((current) => ({
        ...current,
        photoPreview: reader.result as string,
      }))
    }
    reader.readAsDataURL(file)

    setPhotoUploading(true)
    try {
      const uploaded = await uploadFile(file, "child-photo")
      setForm((current) => ({ ...current, photo: uploaded }))
      toast.success("Photo uploaded")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload photo")
    } finally {
      setPhotoUploading(false)
    }
  }

  const handleDocumentAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ""
    if (files.length === 0 || documentUploading) return

    setDocumentUploading(true)
    try {
      const results = await Promise.allSettled(files.map((file) => uploadFile(file, "child-document")))
      const uploadedDocuments = results.flatMap((result) =>
        result.status === "fulfilled" ? [{ name: result.value.name, file: result.value }] : [],
      )
      const failedCount = results.length - uploadedDocuments.length

      if (uploadedDocuments.length > 0) {
        setForm((current) => ({
          ...current,
          documents: [...current.documents, ...uploadedDocuments],
        }))
        toast.success(`${uploadedDocuments.length} document${uploadedDocuments.length === 1 ? "" : "s"} uploaded`)
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} document${failedCount === 1 ? "" : "s"} could not be uploaded`)
      }
    } finally {
      setDocumentUploading(false)
    }
  }

  const handleDocumentRemove = (index: number) => {
    setForm({
      ...form,
      documents: form.documents.filter((_, i) => i !== index),
    })
  }

  const handleSaveDraft = async () => {
    setSavingDraft(true)
    try {
      const json = { data: form as unknown as Record<string, unknown> }
      const res = draftId
        ? await client.api.admin.children.drafts[":id"].$patch({ param: { id: draftId }, json })
        : await client.api.admin.children.drafts.$post({ json })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setDraftId(body.data.id)
      toast.success("Draft saved")
    } catch {
      toast.error("Failed to save draft")
    } finally {
      setSavingDraft(false)
    }
  }

  const validateForm = (): string | null => {
    if (!form.fullName.trim()) return "Full name is required"
    if (!form.dateOfBirth) return "Date of birth is required"
    return null
  }

  const handleSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      toast.error(validationError)
      return
    }

    setSaving(true)
    try {
      const nameParts = form.fullName.trim().split(/\s+/)
      const firstName = nameParts[0] || ""
      const lastName = nameParts.slice(1).join(" ") || firstName
      const photo = form.photo
      const documents = form.documents.map((document) => ({
        ...document.file,
        purpose: "child-document" as const,
      }))

      const res = await client.api.admin.children.$post({
        json: {
          firstName,
          lastName,
          preferredName: form.preferredName.trim() || undefined,
          dateOfBirth: new Date(form.dateOfBirth).toISOString(),
          gender: form.gender.toLowerCase() as "male" | "female",
          classLevel: form.classLevel,
          school: form.school.trim(),
          state: form.state,
          lga: form.lga.trim() || undefined,
          nominatedBy: form.guardianName.trim() || undefined,
          nominatorContact: form.guardianPhone.trim() || form.guardianEmail.trim() || undefined,
          notes: form.familyCircumstances.trim() || undefined,
          guardianName: form.guardianName.trim() || undefined,
          guardianPhone: form.guardianPhone.trim() || undefined,
          guardianEmail: form.guardianEmail.trim() || undefined,
          guardianRelationship: form.guardianRelationship || undefined,
          householdSize: form.householdSize ? Number(form.householdSize) : undefined,
          address: form.address.trim() || undefined,
          familyCircumstances: form.familyCircumstances.trim() || undefined,
          needsSponsorship: form.needsSponsorship,
          sponsorshipNotes: form.sponsorshipNotes.trim() || undefined,
          photoKey: photo?.key,
          documents,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if (draftId) {
        await client.api.admin.children.drafts[":id"].$delete({ param: { id: draftId } })
      }
      toast.success("Child added to the program")
      navigate("/admin/children")
    } catch {
      toast.error("Failed to add child")
    } finally {
      setSaving(false)
    }
  }

  if (!canMutate) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Add Child</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to add children.</p>
        </div>
      </div>
    )
  }

  if (loadingDraft) {
    return <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center"><p className="text-ink/50">Loading draft...</p></div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/children")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-ink/50">{draftId ? "Saved Draft" : "New Record"}</p>
            <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">
              Add a child to the program
            </h1>
            <p className="mt-1 text-sm text-ink/60">
              Follow the steps to onboard a verified child. You can save as draft at any time.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSaveDraft}
              disabled={savingDraft}
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100 disabled:opacity-50"
            >
              <SaveIcon className="h-4 w-4" />
              {savingDraft ? "Saving..." : "Save draft"}
            </button>
            <button
              onClick={() => navigate("/admin/children")}
              className="rounded-full border border-ink/20 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Step Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-full border border-gold-500/50 bg-cream-50 p-1">
        {STEPS.map((step, index) => {
          const isActive = step.key === currentStep
          const isPast = index < currentStepIndex
          return (
            <button
              key={step.key}
              onClick={() => setCurrentStep(step.key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white text-ink shadow-sm"
                  : isPast
                    ? "text-forest-700 hover:bg-white/50"
                    : "text-ink/40 hover:text-ink/60"
              }`}
            >
              {isPast && <CheckIcon className="h-3.5 w-3.5 text-forest-700" />}
              {step.label}
            </button>
          )
        })}
      </div>

      {/* Step Content */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-6">
        {currentStep === "basics" && (
          <div className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink">
                  Full name <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  required
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Enter full name"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Preferred name</span>
                <input
                  type="text"
                  value={form.preferredName}
                  onChange={(e) => setForm({ ...form, preferredName: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Nickname or preferred name"
                />
              </label>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <DatePicker
                label="Date of birth *"
                value={form.dateOfBirth}
                onChange={(value) => setForm({ ...form, dateOfBirth: value })}
                max={new Date()}
              />
              <Select
                label="Gender"
                value={form.gender}
                onChange={(value) => setForm({ ...form, gender: value })}
                options={GENDERS}
              />
            </div>

            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink">Photo</span>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
              <div
                onClick={() => photoInputRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gold-500 bg-cream-50 p-8 transition-colors hover:border-forest-700 hover:bg-cream-100"
              >
                {form.photoPreview ? (
                  <div className="flex flex-col items-center gap-3">
                    <img
                      src={form.photoPreview}
                      alt="Preview"
                      className="h-24 w-24 rounded-full object-cover"
                    />
                    <p className="text-sm text-ink/60">
                      {photoUploading ? "Uploading photo..." : "Click to change photo"}
                    </p>
                  </div>
                ) : (
                  <>
                    <UploadIcon className="h-8 w-8 text-ink/30" />
                    <p className="mt-2 text-sm text-ink/60">Click to upload photo.jpg</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === "family" && (
          <div className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Guardian name</span>
                <input
                  type="text"
                  value={form.guardianName}
                  onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Full name of guardian"
                />
              </label>
              <Select
                label="Relationship"
                value={form.guardianRelationship}
                onChange={(value) => setForm({ ...form, guardianRelationship: value })}
                options={RELATIONSHIPS}
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Phone number</span>
                <input
                  type="tel"
                  value={form.guardianPhone}
                  onChange={(e) => setForm({ ...form, guardianPhone: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="+234 XXX XXX XXXX"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Email</span>
                <input
                  type="email"
                  value={form.guardianEmail}
                  onChange={(e) => setForm({ ...form, guardianEmail: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="guardian@email.com"
                />
              </label>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Household size</span>
                <input
                  type="number"
                  min="1"
                  value={form.householdSize}
                  onChange={(e) => setForm({ ...form, householdSize: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Number of people"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Address</span>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Home address"
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink">Family circumstances</span>
              <textarea
                value={form.familyCircumstances}
                onChange={(e) => setForm({ ...form, familyCircumstances: e.target.value })}
                rows={4}
                className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder="Describe the family's living situation, income sources, challenges..."
              />
            </label>
          </div>
        )}

        {currentStep === "school" && (
          <div className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink">
                  School <span className="text-xs font-normal text-ink/50">(optional)</span>
                </span>
                <input
                  type="text"
                  value={form.school}
                  onChange={(e) => setForm({ ...form, school: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Leave blank if not known"
                />
              </label>
              <Select
                label="Class level"
                value={form.classLevel}
                onChange={(value) => setForm({ ...form, classLevel: value })}
                options={CLASS_LEVELS}
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <Select
                label="State"
                value={form.state}
                onChange={(value) => setForm({ ...form, state: value })}
                options={NIGERIAN_STATES}
              />
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">LGA</span>
                <input
                  type="text"
                  value={form.lga}
                  onChange={(e) => setForm({ ...form, lga: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Local Government Area"
                />
              </label>
            </div>
          </div>
        )}

        {currentStep === "sponsorship" && (
          <div className="space-y-6">
            <div>
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.needsSponsorship}
                  onChange={(e) => setForm({ ...form, needsSponsorship: e.target.checked })}
                  className="h-5 w-5 rounded border-gold-500 text-forest-700 focus:ring-forest-700"
                />
                <span className="text-sm font-medium text-ink">
                  This child needs sponsorship
                </span>
              </label>
              <p className="mt-1.5 ml-8 text-sm text-ink/60">
                Mark this if the child is eligible and seeking sponsorship through the program.
              </p>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink">Sponsorship notes</span>
              <textarea
                value={form.sponsorshipNotes}
                onChange={(e) => setForm({ ...form, sponsorshipNotes: e.target.value })}
                rows={4}
                className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder="Any specific sponsorship requirements, preferences, or notes..."
              />
            </label>
          </div>
        )}

        {currentStep === "documents" && (
          <div className="space-y-6">
            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink">Upload documents</span>
              <p className="mb-3 text-sm text-ink/60">
                Upload birth certificate, guardian ID, consent forms, and other relevant documents.
              </p>
              <input
                ref={documentInputRef}
                type="file"
                multiple
                onChange={handleDocumentAdd}
                className="hidden"
              />
              <div
                onClick={() => documentInputRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gold-500 bg-cream-50 p-8 transition-colors hover:border-forest-700 hover:bg-cream-100"
              >
                <UploadIcon className="h-8 w-8 text-ink/30" />
                <p className="mt-2 text-sm text-ink/60">
                  {documentUploading ? "Uploading..." : "Click to select a file"}
                </p>
              </div>
            </div>

            {form.documents.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-ink">Uploaded files</span>
                <div className="divide-y divide-gold-500/30 rounded-lg border border-gold-500 bg-cream-50">
                  {form.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-ink">{doc.name}</span>
                      <button
                        onClick={() => handleDocumentRemove(index)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === "review" && (
          <div className="space-y-6">
            <div className="rounded-lg bg-cream-100 p-4">
              <p className="text-sm font-medium text-ink">Review and submit</p>
              <p className="mt-1 text-sm text-ink/70">
                Please review all the information before submitting. You can go back to any step to make changes.
              </p>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-forest-700">Basics</h3>
                <div className="mt-3 grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink/60">Full name</span>
                    <span className="font-medium text-ink">{form.fullName || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Preferred name</span>
                    <span className="font-medium text-ink">{form.preferredName || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Date of birth</span>
                    <span className="font-medium text-ink">{form.dateOfBirth || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Gender</span>
                    <span className="font-medium text-ink">{form.gender}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Photo</span>
                    <span className="font-medium text-ink">{form.photo ? "Uploaded" : "—"}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-forest-700">Family & Guardian</h3>
                <div className="mt-3 grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink/60">Guardian</span>
                    <span className="font-medium text-ink">{form.guardianName || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Relationship</span>
                    <span className="font-medium text-ink">{form.guardianRelationship}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Phone</span>
                    <span className="font-medium text-ink">{form.guardianPhone || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Email</span>
                    <span className="font-medium text-ink">{form.guardianEmail || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-forest-700">School & Academics</h3>
                <div className="mt-3 grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink/60">School</span>
                    <span className="font-medium text-ink">{form.school || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">Class</span>
                    <span className="font-medium text-ink">{form.classLevel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">State</span>
                    <span className="font-medium text-ink">{form.state}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">LGA</span>
                    <span className="font-medium text-ink">{form.lga || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-forest-700">Documents</h3>
                <div className="mt-3 text-sm">
                  {form.documents.length > 0 ? (
                    <ul className="list-inside list-disc text-ink">
                      {form.documents.map((doc, i) => (
                        <li key={i}>{doc.name}</li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-ink/60">No documents uploaded</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={isFirstStep}
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100 disabled:opacity-30"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>

        {isLastStep ? (
          <button
            onClick={handleSubmit}
            disabled={saving || photoUploading || documentUploading}
            className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
          >
            {saving ? "Submitting..." : "Add child to program"}
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            Continue
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
